import { useEffect, useMemo, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { fetchDirectory, companyLabelFor } from "./directoryScope";
import type { DirectoryScope } from "./directoryScope";
import { Selection } from "./useChatData";
import { parseMeta } from "./status";
import { ChevronIcon } from "./Icons";
import Avatar from "./Avatar";
import AvatarHoverCard from "./AvatarHoverCard";
import { seedAvatarFromUser } from "./avatars";
import { seedNameFromUser } from "./names";
import * as sounds from "./sounds";

/**
 * The browsable org directory: Category → Company → People.
 *
 * Only internal team members ever see this (Sidebar decides, via
 * scope.canSeeAll). Externals get no list at all — they reach people
 * through the top search bar, which is scope-limited server-side.
 *
 * Two things keep it usable once there are real numbers in it: companies
 * start collapsed so a 40-person company can't bury the ones below it,
 * and each company's roster is capped until you ask for the rest. Typing
 * in the filter overrides both, because at that point you've already said
 * what you're looking for.
 */

const CATEGORY_ORDER = ["team", "supplier", "customer", "partner", "external"] as const;
type CategoryKey = (typeof CATEGORY_ORDER)[number];

const CATEGORY_LABEL: Record<CategoryKey, string> = {
  team: "Teams",
  supplier: "Suppliers",
  customer: "Customers",
  partner: "Partners",
  external: "External",
};

/** How many people show before "show more" — roughly a screenful next to
 * the other sections, not so few that small teams need a click. */
const ROSTER_CAP = 6;

type Company = { key: string; label: string; people: CometChat.User[] };
type Category = { key: CategoryKey; companies: Company[]; total: number };

export default function DirectoryTree({
  selection,
  onSelect,
  onOpenProfile,
}: {
  selection: Selection | null;
  onSelect: (s: Selection) => void;
  onOpenProfile?: (uid: string) => void;
}) {
  const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
  const [users, setUsers] = useState<CometChat.User[] | null>(null);
  const [scope, setScope] = useState<DirectoryScope | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});
  const [openCompanies, setOpenCompanies] = useState<Record<string, boolean>>({});
  const [expandedRosters, setExpandedRosters] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    fetchDirectory(meUid)
      .then(({ users: list, scope: s }) => {
        if (cancelled) return;
        list.forEach((u) => {
          // These carry avatar + name already; seeding avoids a per-uid
          // lookup when the same person shows up elsewhere.
          seedAvatarFromUser(u);
          seedNameFromUser(u);
        });
        setUsers(list);
        setScope(s);
      })
      .catch((e) => {
        console.warn("directory load failed", e);
        if (!cancelled) {
          setUsers([]);
          setError("Couldn't load the directory — try reopening this tab.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [meUid]);

  const q = query.trim().toLowerCase();

  const categories = useMemo<Category[]>(() => {
    if (!users || !scope) return [];
    const buckets = new Map<CategoryKey, Map<string, Company>>();

    for (const u of users) {
      const org = scope.people[u.getUid()];
      const rawCat = org?.category;
      const cat: CategoryKey =
        rawCat && (CATEGORY_ORDER as readonly string[]).includes(rawCat)
          ? (rawCat as CategoryKey)
          : "external";
      // An unnamed domain still gets a readable heading (see
      // companyFromDomain) rather than showing a raw URL.
      const label = companyLabelFor(org) || "Unknown";
      const key = (org?.company || org?.domain || "unknown").toLowerCase();

      if (!buckets.has(cat)) buckets.set(cat, new Map());
      const companies = buckets.get(cat)!;
      if (!companies.has(key)) companies.set(key, { key, label, people: [] });
      companies.get(key)!.people.push(u);
    }

    const matchesPerson = (u: CometChat.User) => {
      if (!q) return true;
      // Scope email first — it's set for everyone, unlike the metadata
      // copy which only exists once that person has signed in.
      const email =
        scope.people[u.getUid()]?.email || (parseMeta(u).dbmEmail as string | undefined) || "";
      return u.getName().toLowerCase().includes(q) || email.toLowerCase().includes(q);
    };

    return CATEGORY_ORDER.map((key) => {
      const companies: Company[] = [];
      for (const company of buckets.get(key)?.values() || []) {
        const companyMatches = !q || company.label.toLowerCase().includes(q);
        // Matching the company name keeps its whole roster — you searched
        // for the company, not for one person inside it.
        const people = companyMatches ? company.people : company.people.filter(matchesPerson);
        if (people.length === 0) continue;
        companies.push({
          ...company,
          people: people.slice().sort((a, b) => a.getName().localeCompare(b.getName())),
        });
      }
      companies.sort((a, b) => a.label.localeCompare(b.label));
      return { key, companies, total: companies.reduce((n, c) => n + c.people.length, 0) };
    }).filter((c) => c.companies.length > 0);
  }, [users, scope, q]);

  if (users === null) return <div className="sect-empty">Loading directory…</div>;

  const totalShown = categories.reduce((n, c) => n + c.total, 0);

  return (
    <div className="dir">
      <div className="dir__search">
        <input
          type="text"
          placeholder="Filter people or companies…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {!!query && (
          <button className="dir__clear" onClick={() => setQuery("")} aria-label="Clear filter">
            ✕
          </button>
        )}
      </div>

      {error && <div className="sect-empty">{error}</div>}

      {!error && totalShown === 0 && (
        <div className="sect-empty">
          {q ? `No one matches “${query.trim()}”.` : "No one here yet."}
        </div>
      )}

      {categories.map((cat) => {
        // While filtering, everything opens — you've already narrowed it,
        // so making you click through the tree again is just friction.
        const catOpen = q ? true : openCats[cat.key] !== false;
        return (
          <div className="sect dir__cat" key={cat.key}>
            <div className="sect-hrow">
              <button
                className="sect-h"
                onClick={() => setOpenCats((p) => ({ ...p, [cat.key]: catOpen ? false : true }))}
              >
                <span className={`sect-caret ${catOpen ? "" : "is-collapsed"}`}>
                  <ChevronIcon size={11} />
                </span>
                {CATEGORY_LABEL[cat.key]}
                <span className="dir__rule" />
                <span className="dir__count">{cat.total}</span>
              </button>
            </div>

            {catOpen &&
              cat.companies.map((company) => {
                const cKey = `${cat.key}:${company.key}`;
                const companyOpen = q ? true : !!openCompanies[cKey];
                const rosterOpen = q ? true : !!expandedRosters[cKey];
                const shown = rosterOpen ? company.people : company.people.slice(0, ROSTER_CAP);
                const hidden = company.people.length - shown.length;
                return (
                  <div className="dir__co" key={cKey}>
                    <button
                      className="dir__cohead"
                      onClick={() => setOpenCompanies((p) => ({ ...p, [cKey]: !companyOpen }))}
                    >
                      <span className={`sect-caret ${companyOpen ? "" : "is-collapsed"}`}>
                        <ChevronIcon size={10} />
                      </span>
                      <span className="dir__coname">{company.label}</span>
                      <span className="dir__count">{company.people.length}</span>
                    </button>

                    {companyOpen && (
                      <>
                        {shown.map((u) => {
                          const uid = u.getUid();
                          const active = selection?.kind === "user" && selection.id === uid;
                          return (
                            <div className={`item dir__person ${active ? "on" : ""}`} key={uid}>
                              <button
                                className="item-main"
                                onClick={() => {
                                  sounds.playOnce(sounds.SOUND.click);
                                  onSelect({
                                    kind: "user",
                                    id: uid,
                                    name: u.getName(),
                                    subtitle: "Direct message",
                                  });
                                }}
                              >
                                <AvatarHoverCard
                                  uid={uid}
                                  name={u.getName()}
                                  onOpenProfile={onOpenProfile}
                                >
                                  <Avatar uid={uid} name={u.getName()} size="dav" />
                                </AvatarHoverCard>
                                <span className="nm">{u.getName()}</span>
                              </button>
                            </div>
                          );
                        })}
                        {hidden > 0 && (
                          <button
                            className="dir__more"
                            onClick={() => setExpandedRosters((p) => ({ ...p, [cKey]: true }))}
                          >
                            + show {hidden} more
                          </button>
                        )}
                        {rosterOpen && !q && company.people.length > ROSTER_CAP && (
                          <button
                            className="dir__more"
                            onClick={() => setExpandedRosters((p) => ({ ...p, [cKey]: false }))}
                          >
                            show less
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}
